import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSession } from '../../session';
import { api, ApiError, type AdminUserRow, type UserTypeRow } from '../../api';

export function People() {
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const status = params.get('status') ?? '';
  const userTypeId = params.get('userTypeId') ?? '';
  const page = Number(params.get('page') ?? 1);

  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState<UserTypeRow[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const { user } = useSession();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ types: UserTypeRow[] }>('/admin/users/types').then((r) => setTypes(r.types)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const q = new URLSearchParams({ page: String(page), pageSize: '25' });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    if (userTypeId) q.set('userTypeId', userTypeId);
    try {
      const res = await api.get<{ total: number; users: AdminUserRow[] }>(`/admin/users?${q}`);
      setRows(res.users); setTotal(res.total);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  }, [search, status, userTypeId, page]);

  useEffect(() => { void load(); }, [load]);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const pages = Math.max(1, Math.ceil(total / 25));

  // Your own account is never selectable. Bulk actions refuse it anyway —
  // deactivating yourself among fifty others is how a portal ends up with
  // nobody able to sign in — so offering the tick only to refuse it later is
  // a worse way of saying the same thing.
  const shown = rows.map((u) => u.id).filter((id) => id !== user?.id);
  const allShownPicked = shown.length > 0 && shown.every((id) => picked.includes(id));

  return (
    <>
      <div className="page-head">
        <h1 className="h1">People</h1>
        <span className="page-head__actions">
          <Link className="btn btn--ghost" to="/admin/people/import">Import a spreadsheet</Link>
          <Link className="btn btn--primary" to="/admin/people/new">Add someone</Link>
        </span>
      </div>

      <div className="filters">
        <input
          placeholder="Search name, username or email"
          defaultValue={search}
          onKeyDown={(e) => { if (e.key === 'Enter') set('search', (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => set('search', e.target.value.trim())}
          aria-label="Search people"
        />
        <select value={status} onChange={(e) => set('status', e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Deactivated</option>
        </select>
        <select value={userTypeId} onChange={(e) => set('userTypeId', e.target.value)} aria-label="User type">
          <option value="">Any type</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="muted">{total.toLocaleString()} {total === 1 ? 'person' : 'people'}</span>
      </div>

      {error && <p className="auth__error">{error}</p>}

      {picked.length > 0 && (
        <BulkBar picked={picked} types={types}
          onDone={(m) => { setPicked([]); setNote(m); void load(); }}
          onError={setError}
          onClear={() => setPicked([])} />
      )}
      {note && <p className="notice">{note}</p>}

      <table className="grid">
        <thead>
          <tr>
            <th className="tickcol">
              <input type="checkbox" aria-label="Select everyone on this page"
                checked={allShownPicked}
                onChange={() => setPicked(allShownPicked
                  ? picked.filter((id) => !shown.includes(id))
                  : [...new Set([...picked, ...shown])])} />
            </th>
            <th>Name</th><th>Username</th><th>Type</th><th>Courses</th><th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="muted">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={7} className="muted">Nobody matches that.</td></tr>}
          {!loading && rows.map((u) => (
            <tr key={u.id} className={u.status === 'inactive' ? 'is-off' : ''}>
              <td className="tickcol">
                {u.id === user?.id
                  ? <span className="muted small" title="You cannot include your own account">—</span>
                  : <input type="checkbox" checked={picked.includes(u.id)}
                      aria-label={`Select ${u.firstName} ${u.lastName}`}
                      onChange={() => setPicked(picked.includes(u.id)
                        ? picked.filter((id) => id !== u.id) : [...picked, u.id])} />}
              </td>
              <td>
                <Link to={`/admin/people/${u.id}`}>{u.firstName} {u.lastName}</Link>
                {u.status === 'inactive' && <span className="tag">deactivated</span>}
                {!u.hasPassword && <span className="tag tag--wait">no password yet</span>}
                {u.email && <div className="muted small">{u.email}</div>}
              </td>
              <td className="mono">{u.login}</td>
              <td>{u.userTypeName}</td>
              <td className="num">{u.courseCount}</td>
              <td className="muted small">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'never'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {pages > 1 && (
        <div className="pager">
          <button className="btn btn--ghost" disabled={page <= 1} onClick={() => set('page', String(page - 1))}>Previous</button>
          <span className="muted">Page {page} of {pages}</span>
          <button className="btn btn--ghost" disabled={page >= pages} onClick={() => set('page', String(page + 1))}>Next</button>
        </div>
      )}
    </>
  );
}

/**
 * What to do with the people who are ticked.
 *
 * Unenrolling is the one that destroys something, so it asks the server what
 * it would erase before doing it, and says so in words rather than asking for
 * a blanket confirmation nobody reads.
 */
function BulkBar({ picked, types, onDone, onError, onClear }: {
  picked: number[];
  types: UserTypeRow[];
  onDone: (message: string) => void;
  onError: (message: string) => void;
  onClear: () => void;
}) {
  const [action, setAction] = useState('enrol');
  const [courseIds, setCourseIds] = useState<number[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([]);
  const [cohorts, setCohorts] = useState<{ id: number; name: string }[]>([]);
  const [warning, setWarning] = useState<{ error: string; completions: { name: string; course: string }[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const needsCourses = action === 'enrol' || action === 'unenrol';
  const needsCohorts = action === 'add-to-cohort' || action === 'remove-from-cohort';

  useEffect(() => {
    void (async () => {
      try {
        if (needsCourses && !courses.length) {
          const r = await api.get<{ courses: { id: number; name: string }[] }>('/admin/courses?pageSize=200');
          setCourses(r.courses ?? []);
        }
        if (needsCohorts && !cohorts.length) {
          const r = await api.get<{ groups: { id: number; name: string }[] }>('/admin/groups?pageSize=200');
          setCohorts(r.groups ?? []);
        }
      } catch { /* the picker stays empty; the action simply cannot be chosen */ }
    })();
  }, [needsCourses, needsCohorts, courses.length, cohorts.length]);

  const run = async (force: boolean) => {
    setBusy(true); onError('');
    try {
      const res = await api.post<{ action: string; people: number } & Record<string, number>>(
        '/admin/people/bulk',
        { userIds: picked, action, courseIds, groupIds, force },
      );
      setWarning(null);
      const counts = Object.entries(res)
        .filter(([k]) => !['action', 'people'].includes(k))
        .map(([k, v]) => `${v} ${k}`).join(', ');
      onDone(`${res.people} ${res.people === 1 ? 'person' : 'people'}: ${counts}.`);
    } catch (e) {
      const err = e as ApiError;
      // 409 is "this would destroy something" — shown in words rather than
      // turned into a generic error the reader skips past.
      if (err.status === 409 && err.body) {
        setWarning({ error: err.body.error, completions: err.body.completions ?? [] });
      } else {
        onError(err.message);
      }
    } finally { setBusy(false); }
  };

  const ready = !needsCourses && !needsCohorts
    ? true
    : needsCourses ? courseIds.length > 0 : groupIds.length > 0;

  return (
    <section className="panel bulkbar">
      <div className="form">
        <strong>{picked.length} selected</strong>

        <label><span>Do this</span>
          <select value={action} onChange={(e) => { setAction(e.target.value); setWarning(null); }}>
            <option value="enrol">Enrol them on courses</option>
            <option value="unenrol">Take them off courses</option>
            <option value="add-to-cohort">Add to a cohort</option>
            <option value="remove-from-cohort">Remove from a cohort</option>
            <option value="deactivate">Deactivate</option>
            <option value="activate">Reactivate</option>
            <option value="require-password-reset">Ask them to set a new password</option>
          </select></label>

        {needsCourses && (
          <label><span>Courses</span>
            <span className="checklist">
              {courses.map((c) => (
                <label key={c.id} className="check">
                  <input type="checkbox" checked={courseIds.includes(c.id)}
                    onChange={() => { setWarning(null); setCourseIds(courseIds.includes(c.id)
                      ? courseIds.filter((x) => x !== c.id) : [...courseIds, c.id]); }} />
                  {c.name}
                </label>
              ))}
            </span></label>
        )}

        {needsCohorts && (
          <label><span>Cohorts</span>
            <span className="checklist">
              {cohorts.map((g) => (
                <label key={g.id} className="check">
                  <input type="checkbox" checked={groupIds.includes(g.id)}
                    onChange={() => setGroupIds(groupIds.includes(g.id)
                      ? groupIds.filter((x) => x !== g.id) : [...groupIds, g.id])} />
                  {g.name}
                </label>
              ))}
            </span></label>
        )}

        {warning && (
          <div className="notice notice--warn">
            <p>{warning.error}</p>
            {warning.completions.length > 0 && (
              <ul className="plain small">
                {warning.completions.slice(0, 10).map((c, i) => <li key={i}>{c.name} — {c.course}</li>)}
                {warning.completions.length > 10 && <li className="muted">and {warning.completions.length - 10} more</li>}
              </ul>
            )}
            <button className="btn btn--danger" disabled={busy} onClick={() => void run(true)}>
              Erase those records and take them off
            </button>
          </div>
        )}

        <div className="row">
          <button className="btn btn--primary" disabled={busy || !ready} onClick={() => void run(false)}>
            {busy ? 'Working…' : 'Do it'}
          </button>
          <button className="btn btn--ghost" onClick={onClear}>Clear selection</button>
        </div>
      </div>
    </section>
  );
}
